import { Box, Typography } from "@mui/material"
import dynamic from "next/dynamic"
import React, { Suspense } from "react"
import { InvestmentData } from "./utils"

// Simplified component loading with clear mapping
export const KNOWN_COMPONENTS = {
  InvestSimulatorBlock: dynamic(
    () => import("@/ui/Component/InvestSimulatorBlock/InvestSimulatorBlock"),
  ),
  ContentWithSlidersBlock: dynamic(
    () =>
      import("@/ui/Component/ContentWithSlidersBlock/ContentWithSlidersBlock"),
  ),
} as const

// Components that need investment data
export const INVESTMENT_DATA_COMPONENTS = new Set([
  "InvestSimulatorBlock",
  "ContentWithSlidersBlock",
])

// Simplified component cache
export const componentCache = new Map<string, React.ComponentType<any>>()

// Helper Functions
export const loadDynamicComponent = (blockName: string) => {
  if (componentCache.has(blockName)) {
    return componentCache.get(blockName)!
  }

  const DynamicComponent = dynamic(() =>
    import(`@/ui/Component/${blockName}/${blockName}`)
      .then((mod) => mod.default || mod[blockName] || DefaultBlock)
      .catch(() => {
        console.warn(`Failed to load component: ${blockName}`)
        return DefaultBlock
      }),
  )

  componentCache.set(blockName, DynamicComponent)
  return DynamicComponent
}

export const DefaultBlock: React.FC = () => (
  <Box>
    <Typography variant="h1">Block Not Found</Typography>
  </Box>
)

export interface BlockComponentProps {
  blockName: string
  data: any
  investmentData: InvestmentData
}

export const BlockComponent: React.FC<BlockComponentProps> = ({
  blockName,
  data,
  investmentData,
}) => {
  // Use known component if available, otherwise load dynamically
  const Component =
    KNOWN_COMPONENTS[blockName as keyof typeof KNOWN_COMPONENTS] ||
    loadDynamicComponent(blockName)

  const props = INVESTMENT_DATA_COMPONENTS.has(blockName)
    ? { ...data, investmentData }
    : data

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Component {...props} />
    </Suspense>
  )
}
